import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  MdChevronRight,
  MdOutlineDashboard,
  MdRequestPage,
} from "react-icons/md";
import { getAllModulePermission } from "../../Redux/userManagementSlice";
import { IoIosMenu } from "react-icons/io";
import { RxCross2 } from "react-icons/rx";
import { FaBlog, FaProductHunt, FaUserGear } from "react-icons/fa6";
import { GrOrderedList } from "react-icons/gr";
import { PiBroomThin } from "react-icons/pi";
import { BsTruckFlatbed } from "react-icons/bs";
import { RiChatSmile2Fill } from "react-icons/ri";
import { CiSettings } from "react-icons/ci";
import { HiOutlineReceiptTax } from "react-icons/hi";
import { getCookie } from "../../context/useCookies";

const Sidebar = ({
  navbarOpen,
  setNavbarOpen,
  setModuleName,
  setIsExpanded,
  isExpanded,
  isRefreshConfig,
  setHasPermanentOpen,
}) => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.user);
  const permissions = selector?.getAllModulePermissionData?.data?.data;
  const [activeTab, setActiveTab] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isPermanentlyOpen, setIsPermanentlyOpen] = useState(() => {
    return JSON.parse(sessionStorage.getItem("sidebarPermanentState")) || false;
  });
  const location = useLocation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const userDataString = getCookie("eyisionAdmin");
    if (userDataString) {
      try {
        const parsedData = JSON.parse(userDataString);
        setUserData(parsedData);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (userData?.userId) {
      dispatch(getAllModulePermission({ _id: userData.userId }));
    }
  }, [userData, dispatch, isRefreshConfig]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth < 1300) {
      setNavbarOpen(false);
    }
  }, [windowWidth, setNavbarOpen]);

  useEffect(() => {
    const currentPath = location.pathname.split("/")[2];
    if (currentPath) {
      const matchingTab = getSidebarData().find((tab) =>
        tab.subItems.some((item) => item.module_code === currentPath),
      );
      if (matchingTab) {
        setActiveTab(matchingTab.label);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, permissions]);

  const handleMouseEnter = () => {
    if (!isPermanentlyOpen) setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    if (!isPermanentlyOpen) setIsExpanded(false);
  };

  const handleMenuClick = () => {
    const newState = !isPermanentlyOpen;
    setHasPermanentOpen(newState);
    setIsPermanentlyOpen(newState);
    setIsExpanded(newState);
    sessionStorage.setItem("sidebarPermanentState", JSON.stringify(newState));
  };

  const toggleTab = (tabName) => {
    setActiveTab(activeTab === tabName ? null : tabName);
  };

  const getSidebarData = () => {
    if (!permissions) return [];

    const allPermissions = [1, 2].includes(userData?.roleId)
      ? [
          ...permissions.filter(
            (item) => item?.module_code?.module_code !== "product-types",
          ),
        ]
      : [...permissions];

    const groupedByTab = allPermissions.filter((item) => item?.module_code?.tab !== "tax").reduce((acc, curr) => {
      if (
        (!curr.view && !curr.viewAndUpdate) ||
        !curr.module_code ||
        !curr.module_code.tab ||
        !curr.module_code.name
      )
        return acc;
      const tabName = curr.module_code.tab;
      if (!acc[tabName]) {
        acc[tabName] = [];
      }

      acc[tabName].push({
        name: curr.module_code.name,
        label: curr.module_code.name,
        module_code:
          curr?.module_code?.module_code ||
          curr?.module_code?.name.replace(/\s+/g, "-").toLowerCase(),
      });
      return acc;
    }, {});

    return Object.entries(groupedByTab)
      .filter(([tabName]) => tabName.toLowerCase() !== "message")
      .map(([tabName, modules]) => {
        if (
          tabName.toLowerCase() !== "home" &&
          tabName.toLowerCase() !== "settings"
        ) {
          return {
            label: tabName,
            icon: getIconForTab(tabName),
            subItems: modules,
            isSingleItem: false,
          };
        } else {
          return {
            label: tabName,
            icon: getIconForTab(tabName),
            subItems: modules,
            isSingleItem: modules.length === 1,
          };
        }
      });
  };

  const getIconForTab = (tabName) => {
    const iconMap = {
      home: MdOutlineDashboard,
      "product management": FaProductHunt,
      requests: MdRequestPage,
      users: FaUserGear,
      orders: GrOrderedList,
      promotions: PiBroomThin,
      blog: FaBlog,
      "shipping/pickup": BsTruckFlatbed,
      cms: RiChatSmile2Fill,
      settings: CiSettings,
      tax: HiOutlineReceiptTax,
      "social management": FaBlog,
    };
    return iconMap[tabName] || MdOutlineDashboard;
  };

  const sidebarWidth = isExpanded ? "w-64" : "w-16";
  const sidebarTransition = "transition-all duration-300 ease-in-out";

  return (
    <div
      ref={sidebarRef}
      className={`fixed lg:static inset-y-0 bg-[#FFFFFF] ${sidebarWidth} h-full z-[9999] xl:flex flex-col ${sidebarTransition} ${
        navbarOpen ? "" : "hidden lg:flex"
      } shadow-lg`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="sticky top-0 z-10 flex items-center justify-between h-20 px-2 bg-[#FFFFFF] text-white w-full">
        {isExpanded ? (
          <div className="flex justify-between place-items-center gap-14">
            <h2 className="text-xl pl-5 font-semibold flex items-center text-[#FFFFFF] gap-3">
              <img src="/newLogo.png" alt="logo" className="w-auto h-14" />
            </h2>
            <IoIosMenu
              className="text-2xl text-black cursor-pointer hover:bg-[#85325F] hover:text-white hover:rounded-full hover:p-2"
              onClick={handleMenuClick}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center w-full text-black">
            <IoIosMenu
              className="text-2xl cursor-pointer"
              onClick={handleMenuClick}
            />
          </div>
        )}
        {isExpanded && (
          <button
            className="text-gray-500 focus:outline-none lg:hidden"
            onClick={() => setNavbarOpen(false)}
          >
            <RxCross2 size={24} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto thin-scrollbar">
        <nav
          className={`w-full bg-[#FFFFFF] ${
            isExpanded ? "p-4" : "p-2"
          } overflow-hidden overflow-y-auto`}
        >
          <ul>
            {getSidebarData().map((item, index) => {
              const IconComponent = item.icon;
              const isTabActive = activeTab === item.label;
              const hasActiveChild = item.subItems.some(
                (subItem) =>
                  location.pathname === `/app/${subItem.module_code}`,
              );

              if (item.isSingleItem) {
                const subItem = item.subItems[0];
                const isActive =
                  location.pathname === `/app/${subItem.module_code}`;

                return (
                  <li
                    key={index}
                    className={`flex flex-col mt-2 uppercase text-[14px] ${
                      isExpanded ? "" : "items-center"
                    }`}
                  >
                    <Link
                      className={`flex items-center ${
                        isExpanded ? "gap-3" : "justify-center"
                      } p-2 rounded transition-colors duration-200 ${
                        isActive
                          ? "text-black bg-[#f0f0f0]"
                          : "text-black hover:bg-[#f0f0f0] hover:text-black"
                      }`}
                      to={`/app/${subItem.module_code}`}
                      onClick={() => {
                        setNavbarOpen(false);
                        setModuleName(subItem.module_code);
                      }}
                      title={!isExpanded ? item.label : ""}
                    >
                      <IconComponent size={isExpanded ? 22 : 18} />
                      {isExpanded && (
                        <span className="text-xs">{item.label}</span>
                      )}
                    </Link>
                  </li>
                );
              }

              return (
                <li
                  key={index}
                  className={`flex flex-col mt-2 uppercase text-[14px] ${
                    isExpanded ? "" : "items-center"
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isExpanded ? "gap-3" : "justify-center"
                    } cursor-pointer p-2 rounded transition-colors duration-200 ${
                      hasActiveChild
                        ? "bg-[#f0f0f0] text-black"
                        : "text-black hover:bg-[#f0f0f0] hover:text-black"
                    }`}
                    onClick={() => toggleTab(item.label)}
                    title={!isExpanded ? item.label : ""}
                  >
                    <IconComponent size={isExpanded ? 22 : 18} />
                    {isExpanded && (
                      <>
                        <span className="text-xs">{item.label}</span>
                        <MdChevronRight
                          className={`ml-auto transition-transform duration-200 ${
                            isTabActive ? "rotate-90" : ""
                          }`}
                        />
                      </>
                    )}
                  </div>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isTabActive ? "h-full" : "max-h-0"
                    }`}
                  >
                    {isExpanded && (
                      <ul className="mt-1 ml-6 space-y-1">
                        {item?.subItems?.map((subItem, subIndex) => {
                          const isSubActive =
                            location.pathname === `/app/${subItem.module_code}`;

                          return (
                            <li
                              key={subIndex}
                              className="flex items-center gap-3 mt-2 transition-opacity duration-300"
                              style={{
                                opacity: isTabActive ? 1 : 0,
                                transitionDelay: isTabActive
                                  ? `${subIndex * 50}ms`
                                  : "0ms",
                              }}
                            >
                              <Link
                                className={`flex items-center gap-3 p-2 text-sm transition-all duration-200 ease-in-out rounded ${
                                  isSubActive
                                    ? " text-black font-medium"
                                    : "text-gray-600 hover:bg-[#f0f0f0] hover:text-black"
                                }`}
                                to={`/app/${subItem.module_code}`}
                                onClick={() => {
                                  setNavbarOpen(false);
                                  setModuleName(subItem.module_code);
                                }}
                              >
                                <span className="w-1 h-1 rounded-full bg-black text-black"></span>
                                <span className="text-xs capitalize">
                                  {subItem.label}
                                </span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
